import React, {useEffect, useState} from 'react';
import {Button} from '@chakra-ui/react';
import {Download} from '@carbon/icons-react';
import {AiFieldDefinition} from '../../../shared/fields/enrichment';
import {
  useGetAnalysisRunResults,
  useLatestFieldDefinitionAnalysisRun,
} from '../../../hooks/api/fieldDefinitionAnalysisRun';
import {useIsAnalysisRunInProgress} from './hooks/useIsAnalysisRunInProgress';
import {useCustomer} from '../../../hooks/api/metadata';
import {useToast} from '@chakra-ui/react';
import {Parser} from '@json2csv/plainjs';
import FileSaver from 'file-saver';
import {getFieldLabel} from '../../../shared/fields/types';

export const DownloadAnalysisRunResultsButton = ({
  fieldDefinition,
}: {
  fieldDefinition: AiFieldDefinition;
}) => {
  const customer = useCustomer();
  const {data: analysisRun} = useLatestFieldDefinitionAnalysisRun({
    fieldDefinitionId: fieldDefinition.id,
  });
  const toast = useToast();

  const [isDownloadingResults, setIsDownloadingResults] = useState(false);
  const {
    data: analysisRunResults,
    isError: isAnalysisRunResultsError,
    isFetching: isAnalysisRunResultsFetching,
    isSuccess: isAnalysisRunResultsSuccess,
  } = useGetAnalysisRunResults(
    !analysisRun ? 'skipToken' : {fieldDefinitionRunId: analysisRun._id},
    {
      enabled: isDownloadingResults,
    }
  );

  const isAnalysisRunInProgress = useIsAnalysisRunInProgress({
    fieldDefinitionId: fieldDefinition.id,
  });

  const handleDownload = () => {
    if (!analysisRun) {
      return;
    }
    setIsDownloadingResults(true);
  };

  useEffect(() => {
    if (isDownloadingResults && isAnalysisRunResultsError) {
      toast({
        title: 'Error downloading analysis results.',
        description: 'Please try again or contact support.',
        status: 'error',
      });
      setIsDownloadingResults(false);
    }
  }, [isAnalysisRunResultsError, isDownloadingResults, toast]);

  useEffect(() => {
    if (
      !isDownloadingResults ||
      isAnalysisRunResultsFetching ||
      !isAnalysisRunResultsSuccess
    ) {
      return;
    }

    const csv = new Parser().parse(
      analysisRunResults.map((result) => {
        return {
          Domain: result.domain,
          Lists: result.testListLabels.join(', '),
          Value: result.value,
          Reasoning: result.reasoning,
        };
      })
    );
    const blob = new Blob([csv], {type: 'text/csv'});
    FileSaver.saveAs(
      blob,
      `Analysis Run-${customer.name}-${getFieldLabel(fieldDefinition)}`
    );

    setIsDownloadingResults(false);
  }, [
    analysisRun,
    analysisRunResults,
    customer.name,
    isAnalysisRunResultsFetching,
    isAnalysisRunResultsSuccess,
    isDownloadingResults,
    fieldDefinition,
  ]);

  return (
    <Button
      isDisabled={!analysisRun || isAnalysisRunInProgress}
      isLoading={isDownloadingResults}
      onClick={handleDownload}
      variant="ghost"
    >
      <Download />
    </Button>
  );
};
